import type { CoverageType } from '@/models/insuranceCoverage'
import type { PremiumRange } from '@/models/insuranceTypes'
import {
  type CoverageOutcome,
  getTotalPremium,
  type MinimalQuoteCard,
  type MinimalQuoteCardStage,
  NEGATIVE_OUTCOME_STAGES,
  type PricedQuoteCard,
  PricingKind,
  type QuoteCardData,
  QuoteCardStage,
} from './quoteCardTypes'

/**
 * Identity of the offer a card represents, or null when the card must stand alone.
 *
 * Rocklake rates each line of business on its own portal, so one carrier answering on two
 * lines arrives as two indications — one offer to the customer, whether or not either line
 * came back with a price. `offerGroup` is the token the dispatch that wrote them stamped, so
 * membership is recorded rather than inferred: a hand-added or superseded row carries a
 * different token, or none, and never folds in.
 *
 * The rest of the key splits pairs that share a token but not an offer: `carrierId` because a
 * dispatch spans every carrier at once, and `carrierProgram` because Rocklake returns four
 * Chubb entities and four Palomar ones per application.
 */
export function offerGroupKey(card: QuoteCardData): string | null {
  // Two uploads naming one carrier are two documents the customer holds, not one offer.
  if (card.stage === QuoteCardStage.PRICED && card.isCustomerUploaded) return null
  if (!card.offerGroup) return null
  // No carrier record means the card falls back to vendor branding — grouping would fuse unrelated rows.
  if (!card.carrierId) return null
  return `${groupRound(card)}:${card.carrierId}:${card.carrierProgram ?? ''}`
}

/** The send group split into live and superseded rows, so a replaced Rocklake price never joins the live line. */
function groupRound(card: QuoteCardData): string {
  const superseded = card.stage === QuoteCardStage.PRICED && card.supersededAt
  return superseded ? `${card.offerGroup}:superseded` : `${card.offerGroup}`
}

/** Earliest of the two ISO timestamps, treating null/undefined as "not set". */
function earliest(a: string | null | undefined, b: string | null | undefined): string | null {
  if (!a) return b ?? null
  if (!b) return a
  return a < b ? a : b
}

/** Every line a member speaks for: the one it rated, plus any it actually priced. */
function linesOf(card: QuoteCardData): CoverageType[] {
  const priced = card.stage === QuoteCardStage.PRICED ? card.coverages.map(({ type }) => type) : []
  return card.coverageLine ? [...new Set([card.coverageLine, ...priced])] : priced
}

/**
 * The paper a card merges under, or null when it has to stand alone. Adds to
 * {@link offerGroupKey} the one thing grouping needs beyond identity: a line we can name.
 * A member we can't place could be a re-pricing of any other in the group, so it keeps to
 * itself rather than dragging its siblings out of the merge with it.
 */
function paperKey(card: QuoteCardData): string | null {
  return linesOf(card).length ? offerGroupKey(card) : null
}

/**
 * The brand a card merges under. `carrierName` is the carrier record's display name, so
 * Rocklake's four Palomar papers and three Chubb ones already share one — which is what
 * lets a brand that only said no read as one answer rather than four identical cards.
 */
function brandKey(card: QuoteCardData): string | null {
  return paperKey(card) && `${groupRound(card)}:${card.carrierName}`
}

/**
 * Whether these members are lines of one offer rather than re-pricings of the same one.
 *
 * Staff add offers by hand, and a carrier re-quoted three times is three rows carrying no
 * indication_source to tell them apart — production application f742902a holds exactly that.
 * Merging those would bill Auto Liability once per revision, so an overlap means these rows
 * are versions of one answer, not parts of one.
 */
function coversDistinctLines(members: QuoteCardData[]): boolean {
  const seen = new Set<CoverageType>()
  for (const member of members) {
    for (const line of linesOf(member)) {
      if (seen.has(line)) return false
      seen.add(line)
    }
  }
  return true
}

/**
 * Stage an all-unpriced group settles on. Anything still moving outranks a settled
 * outcome, and a technical failure outranks a decline so it isn't laundered into "the
 * carrier said no".
 */
const UNPRICED_STAGE_ORDER: readonly MinimalQuoteCardStage[] = [
  QuoteCardStage.BOUND,
  QuoteCardStage.REQUEST_TO_BIND,
  QuoteCardStage.APPLICATION_SENT,
  QuoteCardStage.PREPARING,
  QuoteCardStage.ERROR,
  QuoteCardStage.DECLINED,
  QuoteCardStage.UNAVAILABLE,
]

/** Every indication a member already speaks for — one, unless an earlier pass merged it. */
function memberIdsOf(card: QuoteCardData): string[] {
  return card.memberIds ?? [card.id]
}

/** Every indication a merged card speaks for, in one order whatever the payload's was. */
function mergedMemberIds(members: QuoteCardData[]): string[] {
  return [...new Set(members.flatMap(memberIdsOf))].sort()
}

/** The one line a member without a price rated, where it named one. */
function unpricedOutcomesOf(member: QuoteCardData): CoverageOutcome[] {
  if (member.stage === QuoteCardStage.PRICED || !member.coverageLine) return []
  return [{ type: member.coverageLine, stage: member.stage, indicationId: member.id }]
}

/**
 * One entry per line the carrier answered on without a price — one per *line*, not per
 * row: two rows can answer on the same line, and the customer needs to read it once.
 * Where they disagree the furthest-along answer speaks for the line, so a carrier still
 * working somewhere isn't reported as having closed the door.
 */
function outcomesOf(members: QuoteCardData[]): CoverageOutcome[] {
  const byLine = new Map<CoverageType, CoverageOutcome>()
  for (const member of members) {
    for (const outcome of unpricedOutcomesOf(member)) {
      const held = byLine.get(outcome.type)
      if (held && UNPRICED_STAGE_ORDER.indexOf(held.stage) <= UNPRICED_STAGE_ORDER.indexOf(outcome.stage))
        continue
      byLine.set(outcome.type, outcome)
    }
  }
  return [...byLine.values()]
}

/** The members' totals as one range, a member without a range counting its total at both ends. */
function mergedPremiumRange(priced: PricedQuoteCard[]): PremiumRange | null {
  if (!priced.some((member) => member.totalPremiumRange)) return null
  return priced.reduce(
    (range, member) => ({
      low: range.low + (member.totalPremiumRange?.low ?? getTotalPremium(member)),
      high: range.high + (member.totalPremiumRange?.high ?? getTotalPremium(member)),
    }),
    { low: 0, high: 0 }
  )
}

/**
 * Folds a carrier's separately-submitted lines into the single offer they are. Primary is the
 * smallest id among the *priced* members: `InsuranceApplication.indications` has no ordering,
 * an unstable primary would change the card id — and its cart keys — per render, and the id
 * has to name a row that actually carries a price.
 */
function mergePriced(members: QuoteCardData[]): PricedQuoteCard {
  const ordered = [...members].sort((a, b) => a.id.localeCompare(b.id))
  const priced = ordered.filter((member): member is PricedQuoteCard => member.stage === QuoteCardStage.PRICED)
  const [primary] = priced
  const outcomes = outcomesOf(ordered)
  // Part estimate means the whole card is an estimate — a firm tag would overstate the rest.
  const isEstimate = priced.some((member) => member.pricingKind === PricingKind.INDICATION)

  return {
    ...primary,
    // A merged card speaks for several lines, so no single one names it.
    coverageLine: null,
    // Every member, priced or not: checkout and the staff lookup both key off this.
    memberIds: mergedMemberIds(ordered),
    // Undefined, not [], when every line priced: the card keeps the shape it had before.
    coverageOutcomes: outcomes.length ? outcomes : undefined,
    coverages: priced.flatMap((member) => member.coverages),
    oneTimeFees: priced.flatMap((member) => member.oneTimeFees ?? []),
    totalPremium: priced.reduce((sum, member) => sum + getTotalPremium(member), 0),
    offerTotals: Object.fromEntries(priced.map((member) => [member.id, getTotalPremium(member)])),
    totalPremiumRange: mergedPremiumRange(priced),
    pricingKind: isEstimate ? PricingKind.INDICATION : PricingKind.QUOTED,
    // The expiry reads off this alone, so an estimate must not inherit one from the firm
    // line beside it. Otherwise: conservative, the group is only good for as long as its
    // first line is.
    quotedAt: isEstimate
      ? null
      : priced.reduce<string | null>((acc, member) => earliest(acc, member.quotedAt), null),
    // Every priced member, not any: an applied card hides its CTA, stranding the unapplied lines.
    appliedAt: priced.every((member) => member.appliedAt)
      ? priced.reduce<string | null>((acc, member) => earliest(acc, member.appliedAt), null)
      : null,
    // Checkout applies to every member, so anything that blocks one blocks the card.
    indicationStatus: priced.find((member) => member.indicationStatus)?.indicationStatus ?? null,
    moratorium: priced.find((member) => member.moratorium)?.moratorium ?? null,
    carrierExpired: priced.some((member) => member.carrierExpired),
    expiresAt: priced.reduce<string | null>((acc, member) => earliest(acc, member.expiresAt), null),
    // Every member of a group is one round by construction, so the primary's flag speaks for all.
    supersededAt: primary.supersededAt ?? null,
    // Take whichever member has one so a note or document isn't silently dropped.
    brokerNote: priced.find((member) => member.brokerNote)?.brokerNote ?? null,
    brokerRecommended: priced.some((member) => member.brokerRecommended),
    recommendedByName: priced.find((member) => member.recommendedByName)?.recommendedByName,
    quoteDocuments: priced.flatMap((member) => member.quoteDocuments ?? []),
    isCurrentInsurer: priced.some((member) => member.isCurrentInsurer),
  }
}

/** The same fold for a group where no line came back with a price.
 *
 *  Primary is the smallest id, except that a member holding a carrier record outranks one
 *  that doesn't: only the record names the brand, where a vendor-only row falls back to the
 *  vendor's own label — "Nirvana Insurance" rather than "Nirvana".
 *
 *  No per-line breakdown: with nothing priced there is no split to explain, and listing
 *  the lines only repeats the header's one word once per coverage. */
function mergeUnpriced(members: MinimalQuoteCard[]): MinimalQuoteCard {
  const ordered = [...members].sort(
    (a, b) => Number(!!b.carrierId) - Number(!!a.carrierId) || a.id.localeCompare(b.id)
  )
  const [primary] = ordered
  const stages = ordered.map((member) => member.stage)
  const stage = UNPRICED_STAGE_ORDER.find((candidate) => stages.includes(candidate)) ?? primary.stage

  return {
    ...primary,
    stage,
    // A merged card speaks for several lines, so no single one names it.
    coverageLine: null,
    // Nor for one paper: this card is the brand's answer, not APEX's or SUMMIT's.
    carrierProgram: null,
    memberIds: mergedMemberIds(ordered),
    coverageOutcomes: undefined,
    // Off a member at the stage shown, or a still-waiting card would read as turned away.
    failureReason: ordered.find((member) => member.stage === stage && member.failureReason)?.failureReason,
  }
}

/**
 * The brand whose answer a card is, or null when it must stand alone.
 *
 * Unlike {@link brandKey} this spans dispatches, because a brand can be reached through more
 * than one door: Rocklake places Nirvana while our own Nirvana integration submits to it
 * direct, so production application 975e516f holds two Nirvana cards both reading "Declined
 * to provide quotes." `carrierBrand` is one name for every entity a carrier writes on, so it
 * sees both as Nirvana where `carrierName` reads "Nirvana (Admitted)" against the vendor-only
 * row's "Nirvana Insurance".
 *
 * It is the only thing keyed on — never `carrierName`, which is a label a row renders rather
 * than an identity. A row with no brand keeps to itself, which is what holds an unassigned
 * wholesaler row apart from the next one wearing the same wholesaler's name.
 *
 * Customer uploads are left out, as in {@link offerGroupKey}: a document the customer holds
 * from another broker is not this submission's answer.
 */
function brandAnswerKey(card: QuoteCardData): string | null {
  if (card.stage === QuoteCardStage.PRICED && card.isCustomerUploaded) return null
  return card.carrierBrand || null
}

/**
 * One card per brand for the answers that carry no price.
 *
 * A carrier saying no is the same news whichever door it came through, so the brand says it
 * once. And where any of its paper did price, the no isn't news at all: the card that priced
 * is the brand's answer, and the settled ones fold into it rather than sitting beside it
 * repeating the name under a decline.
 *
 * Only a *settled* no folds into a price. A brand still preparing somewhere, waiting on a
 * document, at checkout or already bound is holding something the customer has to act on or
 * read, and one of its other papers having priced doesn't retire it.
 *
 * Runs on the output of the two grouping passes, not their input — what it collapses is
 * whole offers, so it has to see them assembled.
 */
function collapseBrandAnswers(cards: QuoteCardData[]): QuoteCardData[] {
  const byBrand = collect(cards, brandAnswerKey)
  const emitted = new Set<string>()
  const collapsed: QuoteCardData[] = []
  for (const card of cards) {
    const brand = brandAnswerKey(card)
    const members = (brand && byBrand.get(brand)) || []
    if (!brand || members.length < 2) {
      collapsed.push(card)
      continue
    }
    const priced = members.filter((member) => member.stage === QuoteCardStage.PRICED)
    if (!priced.length) {
      if (emitted.has(brand)) continue
      emitted.add(brand)
      collapsed.push(mergeUnpriced(members as MinimalQuoteCard[]))
      continue
    }
    const settled = members.filter((member) => NEGATIVE_OUTCOME_STAGES.has(member.stage))
    if (!settled.length) {
      collapsed.push(card)
      continue
    }
    if (NEGATIVE_OUTCOME_STAGES.has(card.stage)) continue
    // Folded away, not dropped: visibility and every staff lookup read these ids.
    if (card !== priced[0]) {
      collapsed.push(card)
      continue
    }
    collapsed.push({
      ...card,
      memberIds: mergedMemberIds([card, ...settled]),
    })
  }
  return collapsed
}

/** Bucket each card under the key it merges by, skipping the ones that stand alone. */
function collect(cards: QuoteCardData[], keyOf: (card: QuoteCardData) => string | null) {
  const byKey = new Map<string, QuoteCardData[]>()
  for (const card of cards) {
    const key = keyOf(card)
    if (!key) continue
    const members = byKey.get(key)
    if (members) members.push(card)
    else byKey.set(key, [card])
  }
  return byKey
}

/**
 * Collapses cards that represent the same offer, holding each group at the position of its
 * first member so the caller's ordering survives.
 *
 * Two passes, because a price and a decline are worth different things. A paper that priced
 * anything keeps its own card: Rocklake returns three Chubb entities and four Palomar ones,
 * and when two of them quote, those are two offers the customer has to choose between.
 * A paper that only ever said no has nothing to choose between, so it folds in with the rest
 * of its brand — which is what stops one carrier's declines filling the page.
 *
 * Cards {@link paperKey} won't key — customer-uploaded, or missing a dispatch token, a
 * carrier, or a nameable line — pass through those two untouched, and are then caught by
 * {@link collapseBrandAnswers}, which holds a brand to one card wherever it didn't price.
 */
export function groupQuoteCards(cards: QuoteCardData[]): QuoteCardData[] {
  const byPaper = collect(cards, paperKey)
  const hasPrice = (members: QuoteCardData[]) =>
    members.some((member) => member.stage === QuoteCardStage.PRICED)

  const keyOf = (card: QuoteCardData): string | null => {
    const paper = paperKey(card)
    if (!paper) return null
    return hasPrice(byPaper.get(paper) ?? []) ? paper : brandKey(card)
  }

  const membersByKey = collect(cards, keyOf)
  const emitted = new Set<string>()
  const grouped: QuoteCardData[] = []
  for (const card of cards) {
    const key = keyOf(card)
    if (!key) {
      grouped.push(card)
      continue
    }
    const members = membersByKey.get(key) ?? []
    const priced = hasPrice(members)
    // The distinct-lines guard speaks only for priced rows: it exists to stop a re-priced
    // line being billed once per revision, and a brand's declines carry no price to double.
    if (members.length < 2 || (priced && !coversDistinctLines(members))) {
      grouped.push(card)
      continue
    }
    if (emitted.has(key)) continue
    emitted.add(key)
    grouped.push(priced ? mergePriced(members) : mergeUnpriced(members as MinimalQuoteCard[]))
  }
  return collapseBrandAnswers(grouped)
}
